export type Snapshot = {
  root_hash: string;
  liabilities: string;
  assets: string;
  proof_hash: string;
  circuit_id: string;
  report_hash: string;
};

export type ProofFixture = {
  username: string;
  usernameToBigInt?: string;
  balance: string;
  pathIndices: number[];
  siblingsHashes: string[];
  siblingsSums: string[];
};

export type DemoFixture = {
  id: string;
  label: string;
  description: string;
  expectedPass: boolean;
  report: Record<string, unknown>;
  snapshot: Snapshot;
  proof: ProofFixture;
  diagnostics?: Record<string, unknown>;
};

export type ScenarioSummary = {
  id: string;
  label: string;
  description: string;
  href: string;
  expectedPass: boolean;
};

export type FixtureEditorText = {
  snapshot: string;
  report: string;
  proof: string;
};

export const EXPECTED_CIRCUIT_ID =
  'b3afc376d91aec5873efea96233c7fadabc4cad275d87858992eea01a3f5ae30';

export const snapshot: Snapshot = {
  root_hash: '2b87d12e5a5c02ecf7c52569367bc7c7ae46aab1a900732ff5c93c3f89e445f8',
  liabilities: '84359',
  assets: '100000',
  proof_hash: 'ce67d25a3571775d3b9720680da700e5fa35df87ca929e9056d9bee8e591d72e',
  circuit_id: EXPECTED_CIRCUIT_ID,
  report_hash: '67aac35b21b23db9262544b05819bb846e6306b2cf273ddf1b34cfa835004eb4',
};

export const report = {
  subject: 'GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF',
  attestor: 'GBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBXFR',
  period: '2026-07',
  ledger: 0,
  liabilities: '84359',
  assets: '100000',
};

export const proofFixture: ProofFixture = {
  username: 'gAdsIaKy',
  balance: '7534',
  pathIndices: [0, 0, 0, 0],
  siblingsHashes: [
    '3405497655013061136502768874542176491465275092205995841574082657535821212714',
    '9073769258852821333684172814694730043493073952859860182446488448507962929892',
    '16550478094916159817853981752173183057157213380194412837926872619086259416572',
    '15967473167502558734678743957378157458513676477657583828046601442335894632191',
  ],
  siblingsSums: ['2060', '13505', '17241', '44019'],
};

export const defaultFixture: DemoFixture = {
  id: 'bundled',
  label: 'Bundled demo snapshot',
  description: 'The deterministic fixture bundled directly into the demo build.',
  expectedPass: true,
  report,
  snapshot,
  proof: proofFixture,
};

export function buildPublishCommand(fixture: DemoFixture = defaultFixture): string {
  return `stellar contract invoke \\
  --id <RESERVES_REGISTRY_CONTRACT_ID> \\
  --source verifier \\
  --network testnet \\
  -- publish \\
  --attestor ${String(fixture.report.attestor)} \\
  --subject ${String(fixture.report.subject)} \\
  --verifier <BN254_GROTH16_VERIFIER_CONTRACT_ID> \\
  --snapshot '{"root_hash":"${fixture.snapshot.root_hash}","liabilities":"${fixture.snapshot.liabilities}","assets":"${fixture.snapshot.assets}","proof_hash":"${fixture.snapshot.proof_hash}","circuit_id":"${fixture.snapshot.circuit_id}","report_hash":"${fixture.snapshot.report_hash}"}' \\
  --verification_key '<verification-key-json>' \\
  --proof '<groth16-proof-json>' \\
  --public_signals '<public-signals-json>'`;
}

export const publishCommand = buildPublishCommand(defaultFixture);

export function fixtureToEditorText(fixture: DemoFixture): FixtureEditorText {
  return {
    snapshot: JSON.stringify(fixture.snapshot, null, 2),
    report: JSON.stringify(fixture.report, null, 2),
    proof: JSON.stringify(fixture.proof, null, 2),
  };
}

export function buildFixtureFromEditorText(
  text: FixtureEditorText,
  label = 'Custom local submission',
): DemoFixture {
  return buildFixtureFromUnknown({
    id: 'custom',
    label,
    description: 'Verified from JSON submitted in this browser session.',
    expectedPass: false,
    snapshot: parseJson(text.snapshot, 'snapshot'),
    report: parseJson(text.report, 'report'),
    proof: parseJson(text.proof, 'proof'),
  });
}

export function buildFixtureFromPayloadText(text: string): DemoFixture {
  const payload = parseJson(text, 'fixture');
  return buildFixtureFromUnknown(payload);
}

export function buildFixtureFromUnknown(payload: unknown): DemoFixture {
  const record = assertRecord(payload, 'fixture');
  const snapshot = parseSnapshot(record.snapshot);
  const report = assertRecord(record.report, 'report');
  const proof = parseProof(record.proof);

  return {
    id: typeof record.id === 'string' ? record.id : 'custom',
    label: typeof record.label === 'string' ? record.label : 'Custom local submission',
    description:
      typeof record.description === 'string'
        ? record.description
        : 'Verified from JSON submitted in this browser session.',
    expectedPass: typeof record.expectedPass === 'boolean' ? record.expectedPass : false,
    report,
    snapshot,
    proof,
    diagnostics: assertOptionalRecord(record.diagnostics, 'diagnostics'),
  };
}

function parseJson(text: string, label: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'invalid JSON';
    throw new Error(`Unable to parse ${label} JSON: ${message}`);
  }
}

function parseSnapshot(value: unknown): Snapshot {
  const record = assertRecord(value, 'snapshot');
  return {
    root_hash: assertString(record.root_hash, 'snapshot.root_hash'),
    liabilities: assertString(record.liabilities, 'snapshot.liabilities'),
    assets: assertString(record.assets, 'snapshot.assets'),
    proof_hash: assertString(record.proof_hash, 'snapshot.proof_hash'),
    circuit_id: assertString(record.circuit_id, 'snapshot.circuit_id'),
    report_hash: assertString(record.report_hash, 'snapshot.report_hash'),
  };
}

function parseProof(value: unknown): ProofFixture {
  const record = assertRecord(value, 'proof');
  const proof: ProofFixture = {
    username: assertString(record.username, 'proof.username'),
    balance: assertString(record.balance, 'proof.balance'),
    pathIndices: assertNumberArray(record.pathIndices, 'proof.pathIndices'),
    siblingsHashes: assertStringArray(record.siblingsHashes, 'proof.siblingsHashes'),
    siblingsSums: assertStringArray(record.siblingsSums, 'proof.siblingsSums'),
  };

  if (record.usernameToBigInt !== undefined) {
    proof.usernameToBigInt = assertString(record.usernameToBigInt, 'proof.usernameToBigInt');
  }

  return proof;
}

function assertRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} must be a JSON object`);
  }
  return value as Record<string, unknown>;
}

function assertOptionalRecord(value: unknown, label: string): Record<string, unknown> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return assertRecord(value, label);
}

function assertString(value: unknown, label: string): string {
  if (typeof value !== 'string') {
    throw new Error(`${label} must be a string`);
  }
  return value;
}

function assertNumberArray(value: unknown, label: string): number[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'number')) {
    throw new Error(`${label} must be an array of numbers`);
  }
  return value;
}

function assertStringArray(value: unknown, label: string): string[] {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string')) {
    throw new Error(`${label} must be an array of strings`);
  }
  return value;
}
